import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Spinner, Alert, Modal } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import OfferModal from './OfferModal';
import OfferRequestModal from './OfferRequestModal';
import { fetchLookups, getDisplayValue } from '../../utils/lookupUtils';

const MessageView = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { currentUser, currentProfile } = useAuth();
  const messagesEndRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showCancelOfferModal, setShowCancelOfferModal] = useState(false);
  const [showAcceptOfferModal, setShowAcceptOfferModal] = useState(false);
  const [showDenyOfferModal, setShowDenyOfferModal] = useState(false);
  const [offerCancelLink, setOfferCancelLink] = useState(null);
  const [offerAcceptLink, setOfferAcceptLink] = useState(null);
  const [offerDenyLink, setOfferDenyLink] = useState(null);
  const [offerRequestCancelLink, setOfferRequestCancelLink] = useState(null);
  const [offerRequestLink, setOfferRequestLink] = useState(null);
  const [offerRequestAcceptLink, setOfferRequestAcceptLink] = useState(null);
  const [offerRequestDenyLink, setOfferRequestDenyLink] = useState(null);
  const [offerLink, setOfferLink] = useState(null);
  const [showCancelOfferRequestModal, setShowCancelOfferRequestModal] = useState(false);
  const [showOfferRequestModal, setShowOfferRequestModal] = useState(false);
  const [showAcceptOfferRequestModal, setShowAcceptOfferRequestModal] = useState(false);
  const [showDenyOfferRequestModal, setShowDenyOfferRequestModal] = useState(false);
  const [cancelingOffer, setCancelingOffer] = useState(false);
  const [cancelingOfferRequest, setCancelingOfferRequest] = useState(false);
  const [acceptingOffer, setAcceptingOffer] = useState(false);
  const [denyingOffer, setDenyingOffer] = useState(false);
  const [acceptingOfferRequest, setAcceptingOfferRequest] = useState(false);
  const [denyingOfferRequest, setDenyingOfferRequest] = useState(false);
  const [denyRequestReason, setDenyRequestReason] = useState('');
  const [lookups, setLookups] = useState({});
  const [conversationAccepted, setConversationAccepted] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (username && currentUser) {
      fetchConversation();
    }
  }, [username, currentUser]);

  useEffect(() => {
    // Only scroll to bottom when shouldScrollToBottom is true
    if (shouldScrollToBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, shouldScrollToBottom]);

  // Fetch lookups for offer activities and terms
  useEffect(() => {
    const getLookups = async () => {
      try {
        // Fetch lookups for offer activities and terms
        const lookupData = await fetchLookups(null, null);
        console.log('Fetched lookups:', lookupData);
        setLookups(lookupData);
      } catch (error) {
        console.error('Error fetching lookups:', error);
      }
    };

    getLookups();
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim()) return;

    // Set shouldScrollToBottom to true to scroll to bottom when sending a new message
    setShouldScrollToBottom(true);
    setSending(true);
    setError(null);

    try {
      // Create FormData for the message
      const formData = new FormData();
      formData.append('recipient', username);
      formData.append('message', newMessage);
      formData.append('message_type', 1); // Regular message type

      console.log('Sending message to:', username, 'with content:', newMessage);

      // Send the message to the API
      const response = await api.post('/messages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Message send response:', response.data);

      if (response.status === 201 && response.data.message) {
        // Refresh the conversation to get the latest messages
        await fetchConversation();

        // Clear the message input
        setNewMessage('');
      } else {
        setError('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Function to fetch conversation data
  const fetchConversation = async () => {
    try {
      setLoading(true);
      setError(null);
      // Set shouldScrollToBottom to true to scroll to bottom when initially loading the conversation
      setShouldScrollToBottom(true);

      // Fetch user profile to get user data and favorite status
      const userProfileResponse = await api.get(`/users/${username}/profile`);
      const userData = userProfileResponse.data.user;
      setUser(userData);

      // Determine favorite status based on links in the response
      if (userData?._links) {
        // If unfavorite link exists, the user is already favorited
        const isUserFavorited = 'unfavorite' in userData._links;
        setIsFavorite(isUserFavorited);
      }

      // Fetch all conversations
      const conversationsResponse = await api.get('/conversations');
      console.log('All conversations response:', conversationsResponse.data);

      if (conversationsResponse.data?.conversations) {
        // Log the structure of the conversations
        console.log('Conversations structure:', conversationsResponse.data.conversations.map(c => ({
          id: c.id,
          users: c.users,
          message: c.message,
          links: c._links
        })));
        // Find the conversation with the current user
        const userConversation = conversationsResponse.data.conversations.find(conversation => {
          // Check if any of the users in the conversation links match the current username
          return conversation._links?.users?.some(user => {
            const userHref = user.href;
            const userUsername = userHref.split('/').pop();
            return userUsername === username;
          });
        });

        console.log('Found conversation with user:', userConversation);

        if (userConversation) {
          // Found a conversation with this user
          const conversationId = userConversation.id;

          // Fetch the full conversation with messages
          const conversationResponse = await api.get(`/conversations/${conversationId}`);
          console.log('Conversation details response:', conversationResponse.data);

          // If we have user data in the conversation response, use it
          if (conversationResponse.data?.conversation?.users &&
              conversationResponse.data.conversation.users.length > 0) {
            // Find the user that matches our username parameter
            const matchingUser = conversationResponse.data.conversation.users.find(
              u => u.username === username
            );

            if (matchingUser) {
              console.log('Found user in conversation response:', matchingUser);
              setUser(matchingUser);
            }
          }

          if (conversationResponse.data?.messages) {
            console.log('Messages in conversation:', conversationResponse.data.messages);
            setMessages(conversationResponse.data.messages);
            setConversationId(conversationId);

            // Check for pagination information in the response
            console.log('Checking for pagination links in response:', conversationResponse.data);
            console.log('_links in response:', conversationResponse.data._links);

            // Check if there's a next page link or if the API indicates more pages in any way
            if (conversationResponse.data.pagination.links && conversationResponse.data.pagination.links.next) {
              console.log('Next page link found:', conversationResponse.data.pagination.links.next);
              setHasNextPage(true);
              setNextPageUrl(conversationResponse.data.pagination.links.next.href);
            } else {
              console.log('No pagination information found in response');
              setHasNextPage(false);
              setNextPageUrl(null);
            }

            // Check if the conversation is accepted
            const conversationData = conversationResponse.data.conversation || {};
            const isAccepted = conversationData.accepted === true;
            console.log('Conversation accepted status:', isAccepted);
            setConversationAccepted(isAccepted);

            // Check if the conversation has offer action links
            const conversationLinks = conversationData._links || {};

            // Check for offer_cancel link
            if (conversationLinks.offer_cancel) {
              console.log('Offer cancel link found:', conversationLinks.offer_cancel);
              setOfferCancelLink(conversationLinks.offer_cancel.href);
            } else {
              setOfferCancelLink(null);
            }

            // Check for offer_accept link
            if (conversationLinks.offer_accept) {
              console.log('Offer accept link found:', conversationLinks.offer_accept);
              setOfferAcceptLink(conversationLinks.offer_accept.href);
            } else {
              setOfferAcceptLink(null);
            }

            // Check for offer_deny link
            if (conversationLinks.offer_deny) {
              console.log('Offer deny link found:', conversationLinks.offer_deny);
              setOfferDenyLink(conversationLinks.offer_deny.href);
            } else {
              setOfferDenyLink(null);
            }

            // Check for offer_request_cancel link
            if (conversationLinks.offer_request_cancel) {
              console.log('Offer request cancel link found:', conversationLinks.offer_request_cancel);
              setOfferRequestCancelLink(conversationLinks.offer_request_cancel.href);
            } else {
              setOfferRequestCancelLink(null);
            }

            // Check for offer link (ability to create an offer)
            if (conversationLinks.offer) {
              console.log('Offer link found:', conversationLinks.offer);
              setOfferLink(conversationLinks.offer.href);
            } else {
              setOfferLink(null);
            }

            // Check for offer_request link (ability to create an offer request)
            if (conversationLinks.offer_request) {
              console.log('Offer request link found:', conversationLinks.offer_request);
              setOfferRequestLink(conversationLinks.offer_request.href);
            } else {
              setOfferRequestLink(null);
            }

            // Check for offer_request_accept link
            if (conversationLinks.offer_request_accept) {
              console.log('Offer request accept link found:', conversationLinks.offer_request_accept);
              setOfferRequestAcceptLink(conversationLinks.offer_request_accept.href);
            } else {
              setOfferRequestAcceptLink(null);
            }

            // Check for offer_request_deny link
            if (conversationLinks.offer_request_deny) {
              console.log('Offer request deny link found:', conversationLinks.offer_request_deny);
              setOfferRequestDenyLink(conversationLinks.offer_request_deny.href);
            } else {
              setOfferRequestDenyLink(null);
            }
          } else {
            // No messages in the conversation yet
            console.log('No messages found in conversation');
            setMessages([]);
            setConversationId(conversationId);
            setConversationAccepted(false);
            setOfferCancelLink(null);
            setOfferAcceptLink(null);
            setOfferDenyLink(null);
            setOfferRequestCancelLink(null);
            setOfferRequestAcceptLink(null);
            setOfferRequestDenyLink(null);
            setOfferLink(null);
            setOfferRequestLink(null);
            // Reset pagination state
            setHasNextPage(false);
            setNextPageUrl(null);
          }
        } else {
          // No existing conversation with this user
          // This means we'll create a new conversation when sending the first message
          setMessages([]);
          setConversationId(null);
          setConversationAccepted(false);
          // Reset pagination state
          setHasNextPage(false);
          setNextPageUrl(null);

          console.log('Current user:', currentUser);
          console.log('Current profile:', currentProfile);
          // Allow creating offers and offer requests even without an existing conversation
          setOfferLink('/messages' ? currentProfile?.looking_to === 'Bid on dates' : null); // Use a dummy link that will be replaced by the actual API endpoint
          setOfferRequestLink('/messages' ? currentProfile?.looking_to === 'Get paid to date' : null); // Use a dummy link that will be replaced by the actual API endpoint

          // Reset other offer-related links
          setOfferCancelLink(null);
          setOfferAcceptLink(null);
          setOfferDenyLink(null);
          setOfferRequestCancelLink(null);
          setOfferRequestAcceptLink(null);
          setOfferRequestDenyLink(null);
        }
      } else {
        // No conversations found
        setMessages([]);
        setConversationId(null);
        setConversationAccepted(false);
        // Reset pagination state
        setHasNextPage(false);
        setNextPageUrl(null);

        // Allow creating offers and offer requests even without an existing conversation
        setOfferLink('/messages' ? currentProfile?.looking_to === 'Bid on dates' : null); // Use a dummy link that will be replaced by the actual API endpoint
        setOfferRequestLink('/messages' ? currentProfile?.looking_to === 'Get paid to date' : null); // Use a dummy link that will be replaced by the actual API endpoint

        // Reset other offer-related links
        setOfferCancelLink(null);
        setOfferAcceptLink(null);
        setOfferDenyLink(null);
        setOfferRequestCancelLink(null);
        setOfferRequestAcceptLink(null);
        setOfferRequestDenyLink(null);
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
      setError('Failed to load conversation. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async () => {
    try {
      setSending(true);
      if (isFavorite) {
        await api.post(`/users/${username}/unfavorite`);
        setActionSuccess('User removed from favorites');
      } else {
        await api.post(`/users/${username}/favorite`);
        setActionSuccess('User added to favorites');
      }

      // Update the favorite status
      setIsFavorite(!isFavorite);

      // Refresh the user profile to get updated links
      try {
        const userProfileResponse = await api.get(`/users/${username}/profile`);
        const updatedUserData = userProfileResponse.data.user;

        // Update user data
        setUser(updatedUserData);

        // Update favorite status based on links in the response
        if (updatedUserData?._links) {
          const isUserFavorited = 'unfavorite' in updatedUserData._links;
          setIsFavorite(isUserFavorited);
        }
      } catch (err) {
        console.error('Error refreshing user profile:', err);
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error toggling favorite:', err);
      setError('Failed to update favorite status. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleOfferSuccess = async () => {
    // Refresh the conversation data to get the latest messages including the offer
    await fetchConversation();

    // Log the conversation ID for debugging purposes
    if (conversationId) {
      console.log(`Offer sent in conversation: ${conversationId}`);
    } else {
      console.log('New conversation created for the offer');
    }

    setActionSuccess('Date offer sent successfully!');

    // Clear success message after 3 seconds
    setTimeout(() => {
      setActionSuccess(null);
    }, 3000);
  };

  const handleOfferRequestSuccess = async () => {
    // Refresh the conversation data to get the latest messages including the offer request
    await fetchConversation();

    // Log the conversation ID for debugging purposes
    if (conversationId) {
      console.log(`Offer request sent in conversation: ${conversationId}`);
    } else {
      console.log('New conversation created for the offer request');
    }

    setActionSuccess('Date request sent successfully!');

    // Clear success message after 3 seconds
    setTimeout(() => {
      setActionSuccess(null);
    }, 3000);
  };

  const handleCancelOffer = async () => {
    if (!offerCancelLink) {
      setError('Cannot cancel offer: no cancel link available');
      return;
    }

    try {
      setCancelingOffer(true);

      // Create FormData for the message
      const formData = new FormData();
      formData.append('recipient', username);
      formData.append('message_type', 5); // Offer cancel type
      formData.append('message', 'I am canceling this offer.'); // Default message

      console.log('Canceling offer with:', { recipient: username, message_type: 5 });

      // Send the cancel request to the API
      const response = await api.post('/messages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Offer cancel response:', response.data);

      if (response.status === 201) {
        // Close the modal
        setShowCancelOfferModal(false);

        // Refresh the conversation to get the latest messages
        await fetchConversation();

        // Show success message
        setActionSuccess('Offer canceled successfully!');

        // Clear success message after 3 seconds
        setTimeout(() => {
          setActionSuccess(null);
        }, 3000);
      } else {
        setError('Failed to cancel offer. Please try again.');
      }
    } catch (error) {
      console.error('Error canceling offer:', error);
      setError(error.response?.data?.message || 'Failed to cancel offer. Please try again.');
    } finally {
      setCancelingOffer(false);
    }
  };

  const handleCancelOfferRequest = async () => {
    if (!offerRequestCancelLink) {
      setError('Cannot cancel offer request: no cancel link available');
      return;
    }

    try {
      setCancelingOfferRequest(true);

      // Create FormData for the message
      const formData = new FormData();
      formData.append('recipient', username);
      formData.append('message_type', 9); // Offer request cancel type
      formData.append('message', 'I am canceling my offer request.'); // Default message

      console.log('Canceling offer request with:', { recipient: username, message_type: 9 });

      // Send the cancel request to the API
      const response = await api.post('/messages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Offer request cancel response:', response.data);

      if (response.status === 201) {
        // Close the modal
        setShowCancelOfferRequestModal(false);

        // Refresh the conversation to get the latest messages
        await fetchConversation();

        // Show success message
        setActionSuccess('Offer request canceled successfully!');

        // Clear success message after 3 seconds
        setTimeout(() => {
          setActionSuccess(null);
        }, 3000);
      } else {
        setError('Failed to cancel offer request. Please try again.');
      }
    } catch (error) {
      console.error('Error canceling offer request:', error);
      setError(error.response?.data?.message || 'Failed to cancel offer request. Please try again.');
    } finally {
      setCancelingOfferRequest(false);
    }
  };

  const handleAcceptOffer = async () => {
    if (!offerAcceptLink) {
      setError('Cannot accept offer: no accept link available');
      return;
    }

    try {
      setAcceptingOffer(true);

      // Create FormData for the message
      const formData = new FormData();
      formData.append('recipient', username);
      formData.append('message_type', 3); // Offer accept type
      formData.append('message', 'I accept your offer.'); // Default message

      console.log('Accepting offer with:', { recipient: username, message_type: 3 });

      // Send the accept request to the API
      const response = await api.post('/messages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Offer accept response:', response.data);

      if (response.status === 201) {
        // Close the modal
        setShowAcceptOfferModal(false);

        // Refresh the conversation to get the latest messages
        await fetchConversation();

        // Show success message
        setActionSuccess('Offer accepted successfully!');

        // Clear success message after 3 seconds
        setTimeout(() => {
          setActionSuccess(null);
        }, 3000);
      } else {
        setError('Failed to accept offer. Please try again.');
      }
    } catch (error) {
      console.error('Error accepting offer:', error);
      setError(error.response?.data?.message || 'Failed to accept offer. Please try again.');
    } finally {
      setAcceptingOffer(false);
    }
  };

  const handleDenyOffer = async () => {
    if (!offerDenyLink) {
      setError('Cannot deny offer: no deny link available');
      return;
    }

    try {
      setDenyingOffer(true);

      // Create FormData for the message
      const formData = new FormData();
      formData.append('recipient', username);
      formData.append('message_type', 4); // Offer deny type
      formData.append('message', 'I am declining this offer.'); // Default message

      console.log('Denying offer with:', { recipient: username, message_type: 4 });

      // Send the deny request to the API
      const response = await api.post('/messages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Offer deny response:', response.data);

      if (response.status === 201) {
        // Close the modal
        setShowDenyOfferModal(false);

        // Refresh the conversation to get the latest messages
        await fetchConversation();

        // Show success message
        setActionSuccess('Offer denied successfully.');

        // Clear success message after 3 seconds
        setTimeout(() => {
          setActionSuccess(null);
        }, 3000);
      } else {
        setError('Failed to deny offer. Please try again.');
      }
    } catch (error) {
      console.error('Error denying offer:', error);
      setError(error.response?.data?.message || 'Failed to deny offer. Please try again.');
    } finally {
      setDenyingOffer(false);
    }
  };

  const handleAcceptOfferRequest = async () => {
    if (!offerRequestAcceptLink) {
      setError('Cannot accept request: no accept link available');
      return;
    }

    try {
      setAcceptingOfferRequest(true);

      // Create FormData for the message
      const formData = new FormData();
      formData.append('recipient', username);
      formData.append('message_type', 7); // Offer request accept type
      formData.append('message', 'I accept your date request.'); // Default message

      console.log('Accepting offer request with:', { recipient: username, message_type: 7 });

      // Send the accept request to the API
      const response = await api.post('/messages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Offer request accept response:', response.data);

      if (response.status === 201) {
        // Close the modal
        setShowAcceptOfferRequestModal(false);

        // Refresh the conversation to get the latest messages
        await fetchConversation();

        // Show success message
        setActionSuccess('Date request accepted successfully!');

        // Clear success message after 3 seconds
        setTimeout(() => {
          setActionSuccess(null);
        }, 3000);
      } else {
        setError('Failed to accept date request. Please try again.');
      }
    } catch (error) {
      console.error('Error accepting offer request:', error);
      setError(error.response?.data?.message || 'Failed to accept date request. Please try again.');
    } finally {
      setAcceptingOfferRequest(false);
    }
  };

  // Function to load more messages from next pages
  const handleLoadMoreMessages = async () => {
    if (!nextPageUrl) {
      console.log('No next page URL available');
      return;
    }

    try {
      // Set shouldScrollToBottom to false to prevent scrolling when loading more messages
      setShouldScrollToBottom(false);
      setLoadingMoreMessages(true);
      setError(null);

      console.log('Loading more messages from:', nextPageUrl);

      // Make a direct request to the next page URL
      let response;
      try {
        response = await api.get(nextPageUrl);
        console.log('Next page response:', response.data);
      } catch (error) {
        console.error('Error fetching next page:', error);
        throw error;
      }

      // Check if we got messages in the response
      if (response.data?.messages && response.data.messages.length > 0) {
        console.log(`Found ${response.data.messages.length} messages in the response`);

        // Prepend the new messages to the existing messages
        setMessages(prevMessages => {
          console.log('Current messages:', prevMessages.length);
          console.log('New messages to add:', response.data.messages.length);
          return [...response.data.messages, ...prevMessages];
        });

        // Update pagination information
        if (response.data.pagination.links && response.data.pagination.links.next) {
          console.log('New next page link found:', response.data.pagination.links.next);
          setHasNextPage(true);
          setNextPageUrl(response.data.pagination.links.next.href);
        } else {
          console.log('No more pages available');
          setHasNextPage(false);
          setNextPageUrl(null);
        }
      } else {
        console.log('No additional messages found on next page');
        setHasNextPage(false);
        setNextPageUrl(null);
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
      setError('Failed to load more messages. Please try again.');
    } finally {
      setLoadingMoreMessages(false);
    }
  };

  const handleDenyOfferRequest = async () => {
    if (!offerRequestDenyLink) {
      setError('Cannot deny request: no deny link available');
      return;
    }

    if (!denyRequestReason.trim()) {
      setError('Please provide a reason for denying the request.');
      return;
    }

    try {
      setDenyingOfferRequest(true);

      // Create FormData for the message
      const formData = new FormData();
      formData.append('recipient', username);
      formData.append('message_type', 8); // Offer request deny type
      formData.append('message', denyRequestReason); // Use the reason provided by the user

      console.log('Denying offer request with:', { recipient: username, message_type: 8, message: denyRequestReason });

      // Send the deny request to the API
      const response = await api.post('/messages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Offer request deny response:', response.data);

      if (response.status === 201) {
        // Close the modal
        setShowDenyOfferRequestModal(false);

        // Reset the deny reason
        setDenyRequestReason('');

        // Refresh the conversation to get the latest messages
        await fetchConversation();

        // Show success message
        setActionSuccess('Date request denied successfully.');

        // Clear success message after 3 seconds
        setTimeout(() => {
          setActionSuccess(null);
        }, 3000);
      } else {
        setError('Failed to deny date request. Please try again.');
      }
    } catch (error) {
      console.error('Error denying offer request:', error);
      setError(error.response?.data?.message || 'Failed to deny date request. Please try again.');
    } finally {
      setDenyingOfferRequest(false);
    }
  };

  const formatMessageDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();

    // Get date parts for comparison (year, month, day)
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calculate the difference in days between the dates
    const diffTime = todayDate - messageDate;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      // Today - show time
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      // Yesterday
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      // Older - show date and time
      return date.toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading conversation...</p>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="py-5">
        <Alert variant="danger">User not found or conversation could not be loaded.</Alert>
        <div className="text-center mt-3">
          <Button onClick={() => navigate('/messages')} variant="primary">
            Back to Messages
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {actionSuccess && (
        <Alert variant="success" className="mb-4">
          {actionSuccess}
        </Alert>
      )}
      <Row>
        <Col lg={4} className="mb-4 order-lg-2">
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="text-center p-4">
              <div className="mb-3">
                {user.avatars?.medium ? (
                  <img
                    src={user.avatars.medium}
                    alt={user.username}
                    className="avatar-medium mx-auto d-block"
                  />
                ) : (
                  <div className="avatar-medium mx-auto d-flex align-items-center justify-content-center bg-light rounded-circle">
                    <i className="bi bi-person-fill fs-1 text-secondary"></i>
                  </div>
                )}
              </div>

              <h4 className="fw-bold mb-1">{user.username}</h4>
              <p className="text-muted mb-3">
                {user.age} • {user.city}, {user.region}
              </p>

              <div className="d-flex justify-content-center mb-3">
                {user.online ? (
                  <Badge bg="success" className="px-3 py-2">
                    <i className="bi bi-circle-fill me-1 small"></i>
                    Online
                  </Badge>
                ) : (
                  <Badge bg="secondary" className="px-3 py-2">
                    <i className="bi bi-clock me-1"></i>
                    Last seen {new Date(user.last_seen).toLocaleDateString()}
                  </Badge>
                )}
              </div>

              <div className="d-grid gap-2">
                <Button as={Link} to={`/users/${user.username}`} variant="primary">
                  View Profile
                </Button>
                <Button
                  onClick={handleToggleFavorite}
                  className={isFavorite ? "btn-profile" : "btn-profile"}
                  disabled={sending}
                >
                  <i className={`bi ${isFavorite ? 'bi-heart-fill' : 'bi-heart'} me-2`}></i>
                  {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Only show Date Offers section when conversation is not accepted */}
          {!conversationAccepted && (
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h5 className="fw-bold mb-3">Date Offers</h5>
                <p className="text-muted small mb-3">
                  {offerAcceptLink || offerDenyLink ?
                    `You have a pending date offer from ${user?.username}.` :
                    offerRequestAcceptLink || offerRequestDenyLink ?
                    `You have a pending date request from ${user?.username}.` :
                    offerRequestCancelLink ?
                    `You have sent a date request to ${user?.username}.` :
                    offerLink ?
                    `Send a date offer to ${user?.username} with your proposed terms.` :
                    offerRequestLink ?
                    `Request a date with ${user?.username} with your proposed terms.` :
                    `Date offers and requests are not available at this time.`}
                </p>

                {offerCancelLink ? (
                  <Button
                    variant="outline-danger"
                    className="w-100 mb-2"
                    onClick={() => setShowCancelOfferModal(true)}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Cancel Current Offer
                  </Button>
                ) : offerRequestCancelLink ? (
                  <Button
                    variant="outline-danger"
                    className="w-100 mb-2"
                    onClick={() => setShowCancelOfferRequestModal(true)}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Cancel Date Request
                  </Button>
                ) : offerAcceptLink && offerDenyLink ? (
                  <div className="d-grid gap-2">
                    <Button
                      variant="outline-success"
                      className="w-100"
                      onClick={() => setShowAcceptOfferModal(true)}
                    >
                      <i className="bi bi-check-circle me-2"></i>
                      Accept Offer
                    </Button>
                    <Button
                      variant="outline-warning"
                      className="w-100"
                      onClick={() => setShowDenyOfferModal(true)}
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Decline Offer
                    </Button>
                  </div>
                ) : offerRequestAcceptLink && offerRequestDenyLink ? (
                  <div className="d-grid gap-2">
                    <Button
                      variant="outline-success"
                      className="w-100"
                      onClick={() => setShowAcceptOfferRequestModal(true)}
                    >
                      <i className="bi bi-check-circle me-2"></i>
                      Accept Request
                    </Button>
                    <Button
                      variant="outline-warning"
                      className="w-100"
                      onClick={() => setShowDenyOfferRequestModal(true)}
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Decline Request
                    </Button>
                  </div>
                ) : offerLink ? (
                  <Button
                    variant="outline-primary"
                    className="w-100 mb-2"
                    onClick={() => setShowOfferModal(true)}
                  >
                    <i className="bi bi-calendar-heart me-2"></i>
                    Create Date Offer
                  </Button>
                ) : offerRequestLink ? (
                  <Button
                    variant="outline-primary"
                    className="w-100"
                    onClick={() => setShowOfferRequestModal(true)}
                  >
                    <i className="bi bi-cash-coin me-2"></i>
                    Request a Date
                  </Button>
                ) : (
                  <p className="text-muted small text-center">
                    You cannot create a date offer or request at this time.
                  </p>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Show Date Status when conversation is accepted */}
          {conversationAccepted && (
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h5 className="fw-bold mb-3">Date Status</h5>
                <div className="text-center">
                  <Badge bg="success" className="px-3 py-2 mb-3">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Date Accepted
                  </Badge>
                  <p className="text-muted small">
                    You can now exchange messages with {user?.username}.
                  </p>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={8} className="order-lg-1">
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Button
                  variant="link"
                  className="p-0 me-3 d-lg-none text-dark"
                  onClick={() => navigate('/messages')}
                >
                  <i className="bi bi-arrow-left fs-4"></i>
                </Button>
                <h5 className="mb-0 fw-bold">Conversation with {user?.username}</h5>
              </div>

              <div>
                <Button variant="link" className="text-muted p-0">
                  <i className="bi bi-three-dots-vertical fs-5"></i>
                </Button>
              </div>
            </Card.Header>

            <div className="message-list p-3" style={{ height: '400px', overflowY: 'auto' }}>
              {messages.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-chat-left-text display-4 text-muted"></i>
                  <p className="mt-3 text-muted">No messages yet</p>
                  <p className="text-muted small">
                    Send a message to start the conversation
                  </p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {/* Load More Messages Button */}
                  {(hasNextPage) && messages.length > 0 && (
                    <div className="text-center mb-3">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                            handleLoadMoreMessages();
                        }}
                        disabled={loadingMoreMessages}
                        className="px-4 py-2"
                        style={{ zIndex: 1000 }}
                      >
                        {loadingMoreMessages ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Loading...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-arrow-up me-2"></i>
                            Load More
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {messages.map((message, index) => {
                    // Determine if the message is from the current user
                    // Check if the message has a 'sent' property or use the user link
                    const isCurrentUser = message.sent === true ||
                      (message._links?.user?.href === currentUser._links?.self?.href);

                    const showDate = index === 0 ||
                      new Date(message.created_dt).toDateString() !==
                      new Date(messages[index - 1].created_dt).toDateString();

                    return (
                      <React.Fragment key={message.id}>
                        {showDate && (
                          <div className="text-center my-2">
                            <span className="badge bg-light text-dark px-3 py-2">
                              {new Date(message.created_dt).toLocaleDateString([], {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                        )}

                        <div className={`d-flex ${isCurrentUser ? 'justify-content-end' : 'justify-content-start'}`}>
                          <div className={`message-item ${isCurrentUser ? 'message-sent' : 'message-received'}`} style={{ maxWidth: '75%' }}>
                            {(message.message_type === 'offer' || message.message_type === 2 || message.message_type === '2') ? (
                              <div className="mb-2">
                                <Badge bg="info" className="px-2 py-1">
                                  {getDisplayValue(lookups, 'message_type', 2, 'Date Offer')}
                                </Badge>
                                {message.activity && (
                                  <Badge bg="secondary" className="ms-1 px-2 py-1">
                                    {typeof message.activity === 'string' && isNaN(parseInt(message.activity))
                                      ? message.activity
                                      : `Activity: ${getDisplayValue(lookups, 'offer_activities', message.activity, message.activity)}`}
                                  </Badge>
                                )}
                                {message.terms && (
                                  <Badge bg="secondary" className="ms-1 px-2 py-1">
                                    {typeof message.terms === 'string' && isNaN(parseInt(message.terms))
                                      ? message.terms
                                      : `Terms: ${getDisplayValue(lookups, 'offer_terms', message.terms, message.terms)}`}
                                  </Badge>
                                )}
                              </div>
                            ) : message.message_type === 'offer_cancel' || message.message_type === 5 || message.message_type === '5' ? (
                              <div className="mb-2">
                                <Badge bg="danger" className="px-2 py-1">
                                  {getDisplayValue(lookups, 'message_type', 5, 'Offer Canceled')}
                                </Badge>
                              </div>
                            ) : message.message_type === 'offer_accept' || message.message_type === 3 || message.message_type === '3' ? (
                              <div className="mb-2">
                                <Badge bg="success" className="px-2 py-1">
                                  {getDisplayValue(lookups, 'message_type', 3, 'Offer Accepted')}
                                </Badge>
                              </div>
                            ) : message.message_type === 'offer_deny' || message.message_type === 4 || message.message_type === '4' ? (
                              <div className="mb-2">
                                <Badge bg="warning" className="px-2 py-1">
                                  {getDisplayValue(lookups, 'message_type', 4, 'Offer Denied')}
                                </Badge>
                              </div>
                            ) : message.message_type === 'offer_request' || message.message_type === 6 || message.message_type === '6' ? (
                              <div className="mb-2">
                                <Badge bg="primary" className="px-2 py-1">
                                  {getDisplayValue(lookups, 'message_type', 6, 'Date Request')}
                                </Badge>
                                {message.activity && (
                                  <Badge bg="secondary" className="ms-1 px-2 py-1">
                                    {typeof message.activity === 'string' && isNaN(parseInt(message.activity))
                                      ? message.activity
                                      : `Activity: ${getDisplayValue(lookups, 'offer_activities', message.activity, message.activity)}`}
                                  </Badge>
                                )}
                                {message.terms && (
                                  <Badge bg="secondary" className="ms-1 px-2 py-1">
                                    {typeof message.terms === 'string' && isNaN(parseInt(message.terms))
                                      ? message.terms
                                      : `Terms: ${getDisplayValue(lookups, 'offer_terms', message.terms, message.terms)}`}
                                  </Badge>
                                )}
                              </div>
                            ) : message.message_type === 'offer_request_accept' || message.message_type === 7 || message.message_type === '7' ? (
                              <div className="mb-2">
                                <Badge bg="success" className="px-2 py-1">
                                  {getDisplayValue(lookups, 'message_type', 7, 'Date Request Accepted')}
                                </Badge>
                              </div>
                            ) : message.message_type === 'offer_request_deny' || message.message_type === 8 || message.message_type === '8' ? (
                              <div className="mb-2">
                                <Badge bg="warning" className="px-2 py-1">
                                  {getDisplayValue(lookups, 'message_type', 8, 'Date Request Denied')}
                                </Badge>
                              </div>
                            ) : message.message_type === 'offer_request_cancel' || message.message_type === 9 || message.message_type === '9' ? (
                              <div className="mb-2">
                                <Badge bg="danger" className="px-2 py-1">
                                  {getDisplayValue(lookups, 'message_type', 9, 'Date Request Canceled')}
                                </Badge>
                              </div>
                            ) : null}

                            {/* Only show message content for regular messages (type 1) and date request denial reasons (type 8) */}
                            {(message.message_type === 1 || message.message_type === '1' || message.message_type === 'message') && (
                              <div className="mb-1">
                                {message.message}
                              </div>
                            )}

                            <div className="text-end">
                              <small className="text-muted">
                                {formatMessageDate(message.created_dt)}
                              </small>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            <Card.Footer className="bg-white p-3">
              {error && (
                <Alert variant="danger" className="mb-3 py-2">
                  {error}
                </Alert>
              )}

              {conversationAccepted ? (
                <Form onSubmit={handleSendMessage}>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={sending}
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!newMessage.trim() || sending}
                    >
                      {sending ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <i className="bi bi-send-fill"></i>
                      )}
                    </Button>
                  </div>
                </Form>
              ) : (
                <Alert variant="info" className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  You can send messages after a date offer or request has been accepted.
                </Alert>
              )}
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Offer Modal */}
      <OfferModal
        show={showOfferModal}
        onHide={() => setShowOfferModal(false)}
        recipient={username}
        onSuccess={handleOfferSuccess}
      />

      {/* Offer Request Modal */}
      <OfferRequestModal
        show={showOfferRequestModal}
        onHide={() => setShowOfferRequestModal(false)}
        recipient={username}
        onSuccess={handleOfferRequestSuccess}
      />

      {/* Cancel Offer Modal */}
      <Modal show={showCancelOfferModal} onHide={() => setShowCancelOfferModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Date Offer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to cancel your date offer to {username || 'this user'}?</p>
          <p className="text-muted small">
            This action cannot be undone. You will need to create a new offer if you change your mind.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCancelOfferModal(false)}>
            Keep Offer
          </Button>
          <Button
            variant="danger"
            onClick={handleCancelOffer}
            disabled={cancelingOffer}
          >
            {cancelingOffer ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Canceling...
              </>
            ) : (
              'Cancel Offer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Accept Offer Modal */}
      <Modal show={showAcceptOfferModal} onHide={() => setShowAcceptOfferModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Accept Date Offer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to accept the date offer from {username || 'this user'}?</p>
          <p className="text-muted small">
            By accepting this offer, you agree to the terms and conditions specified in the offer.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowAcceptOfferModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleAcceptOffer}
            disabled={acceptingOffer}
          >
            {acceptingOffer ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Accepting...
              </>
            ) : (
              'Accept Offer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Deny Offer Modal */}
      <Modal show={showDenyOfferModal} onHide={() => setShowDenyOfferModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Decline Date Offer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to decline the date offer from {username || 'this user'}?</p>
          <p className="text-muted small">
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDenyOfferModal(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleDenyOffer}
            disabled={denyingOffer}
          >
            {denyingOffer ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Declining...
              </>
            ) : (
              'Decline Offer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Offer Request Modal */}
      <Modal show={showCancelOfferRequestModal} onHide={() => setShowCancelOfferRequestModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Date Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to cancel your date request to {username || 'this user'}?</p>
          <p className="text-muted small">
            This action cannot be undone. You will need to create a new request if you change your mind.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCancelOfferRequestModal(false)}>
            Keep Request
          </Button>
          <Button
            variant="danger"
            onClick={handleCancelOfferRequest}
            disabled={cancelingOfferRequest}
          >
            {cancelingOfferRequest ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Canceling...
              </>
            ) : (
              'Cancel Request'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Accept Offer Request Modal */}
      <Modal show={showAcceptOfferRequestModal} onHide={() => setShowAcceptOfferRequestModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Accept Date Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to accept the date request from {username || 'this user'}?</p>
          <p className="text-muted small">
            By accepting this request, you agree to the terms and conditions specified in the request.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowAcceptOfferRequestModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleAcceptOfferRequest}
            disabled={acceptingOfferRequest}
          >
            {acceptingOfferRequest ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Accepting...
              </>
            ) : (
              'Accept Request'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Deny Offer Request Modal */}
      <Modal show={showDenyOfferRequestModal} onHide={() => setShowDenyOfferRequestModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Decline Date Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please provide a reason for declining the request from {username || 'this user'}:</p>
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={3}
              value={denyRequestReason}
              onChange={(e) => setDenyRequestReason(e.target.value)}
              placeholder="Enter your reason for declining..."
              required
            />
            <Form.Text className="text-muted">
              Your response will be sent to the user who made the request.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDenyOfferRequestModal(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleDenyOfferRequest}
            disabled={denyingOfferRequest || !denyRequestReason.trim()}
          >
            {denyingOfferRequest ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Declining...
              </>
            ) : (
              'Decline Request'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MessageView;
